import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Font size scale: base multiplier for each preset
const FONT_SIZE_SCALE = {
  smaller: 0.75,   // 12px base
  small: 0.875,    // 14px base
  medium: 1,       // 16px base (default)
  large: 1.125,    // 18px base
  xlarge: 1.25,    // 20px base
  bigger: 1.5     // 24px base
}

// Derivative font sizes based on base
const FONT_SIZES = {
  smaller: (base) => (base * 0.75).toFixed(2) + 'px',   // 12/14/15.75/17.5
  xs: (base) => (base * 0.75).toFixed(2) + 'px',   // 12/14/15.75/17.5
  sm: (base) => (base * 0.875).toFixed(2) + 'px',  // 14/16/18/20
  base: (base) => base + 'px',                       // 16/18/20/22.5
  lg: (base) => (base * 1.125).toFixed(2) + 'px',   // 18/20.25/22.5/25
  xl: (base) => (base * 1.25).toFixed(2) + 'px',    // 20/22.5/25/28
  '2xl': (base) => (base * 1.5).toFixed(2) + 'px',  // 24/27/30/33.75
  '3xl': (base) => (base * 2).toFixed(2) + 'px',     // 32/36/40/45
  bigger: (base) => (base * 1.5).toFixed(2) + 'px'     // 204px base
}

export const useAppStore = defineStore('app', () => {
  const sidebarCollapsed = ref(false)
  const sidebarOpened = ref(true)
  const language = ref('zh')
  const theme = ref('light')
  const fontSize = ref('medium')
  const fontFamily = ref('system')
  const loading = ref(false)
  const loadingText = ref('Loading...')
  
  const clubInfo = ref(null)

  const fontSizeOptions = [
    { label: 'Smaller', value: 'smaller', scale: FONT_SIZE_SCALE.smaller },
    { label: 'Small', value: 'small', scale: FONT_SIZE_SCALE.small },
    { label: 'Medium', value: 'medium', scale: FONT_SIZE_SCALE.medium },
    { label: 'Large', value: 'large', scale: FONT_SIZE_SCALE.large },
    { label: 'Extra Large', value: 'xlarge', scale: FONT_SIZE_SCALE.xlarge },
    { label: 'Bigger', value: 'bigger', scale: FONT_SIZE_SCALE.bigger }
  ]

  const fontFamilyOptions = [
    { label: 'System', value: 'system' },
    { label: 'UKIJ Ekran', value: 'ukij' },
    { label: 'PingFang SC', value: 'pingfang' },
    { label: 'Microsoft YaHei', value: 'microsoft' }
  ]

  const isDark = computed(() => theme.value === 'dark')
  const currentFontSize = computed(() => fontSize.value)
  
  const currentFontFamily = computed(() => fontFamily.value)

  const toggleSidebar = () => {
    sidebarCollapsed.value = !sidebarCollapsed.value
  }

  const toggleSidebarMobile = () => {
    sidebarOpened.value = !sidebarOpened.value
  }

  const closeSidebarMobile = () => {
    sidebarOpened.value = false
  }

  const openSidebarMobile = () => {
    sidebarOpened.value = true
  }

  const setLanguage = (lang) => {
    language.value = lang
    localStorage.setItem('yolal-language', lang)
  }

  const setTheme = (newTheme) => {
    theme.value = newTheme
    localStorage.setItem('yolal-theme', newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
  }

  /**
   * Apply font size by setting CSS custom properties on document root
   * All font sizes scale proportionally from the base size (16px)
   */
  const setFontSize = (size) => {
    fontSize.value = size
    localStorage.setItem('yolal-font-size', size)
    
    const scale = FONT_SIZE_SCALE[size] || FONT_SIZE_SCALE.medium
    const base = 16 * scale  // 14, 16, 18, 20
    
    // Set CSS variables for all font sizes
    const root = document.documentElement
    root.style.setProperty('--font-size-xs', FONT_SIZES.xs(base))
    root.style.setProperty('--font-size-sm', FONT_SIZES.sm(base))
    root.style.setProperty('--font-size-base', FONT_SIZES.base(base))
    root.style.setProperty('--font-size-lg', FONT_SIZES.lg(base))
    root.style.setProperty('--font-size-xl', FONT_SIZES.xl(base))
    root.style.setProperty('--font-size-2xl', FONT_SIZES['2xl'](base))
    root.style.setProperty('--font-size-3xl', FONT_SIZES['3xl'](base))
    
    // Set root font-size for rem-based scaling
    root.style.fontSize = FONT_SIZES.base(base)
    
    // Re-apply font family after size change
    applyFontFamily(fontFamily.value)
  }

  const applyFontFamily = (font) => {
    const fontFamilies = {
      system: '"UKIJ Ekran", -apple-system, BlinkMacSystemFont, "Segoe UI", "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif',
      ukij: '"UKIJ Ekran", sans-serif',
      pingfang: '"PingFang SC", -apple-system, BlinkMacSystemFont, sans-serif',
      microsoft: '"Microsoft YaHei", "PingFang SC", sans-serif'
    }
    
    const fontFamilyValue = fontFamilies[font] || fontFamilies.system
    document.documentElement.style.fontFamily = fontFamilyValue
    document.body.style.fontFamily = fontFamilyValue
  }

  const setFontFamily = (font) => {
    fontFamily.value = font
    localStorage.setItem('yolal-font-family', font)
    document.documentElement.setAttribute('data-font-family', font)
    applyFontFamily(font)
  }

  const showLoading = (text = 'Loading...') => {
    loading.value = true
    loadingText.value = text
  }

  const hideLoading = () => {
    loading.value = false
  }
  
  const setClubInfo = (info) => {
    clubInfo.value = info
  }

  const init = () => {
    const savedLang = localStorage.getItem('yolal-language')
    if (savedLang) {
      language.value = savedLang
      document.body.classList.add('uyghur-font')
    }

    const savedTheme = localStorage.getItem('yolal-theme')
    if (savedTheme) {
      theme.value = savedTheme
      document.documentElement.classList.toggle('dark', savedTheme === 'dark')
    }

    const savedFontSize = localStorage.getItem('yolal-font-size')
    if (savedFontSize && FONT_SIZE_SCALE[savedFontSize]) {
      fontSize.value = savedFontSize
      setFontSize(savedFontSize)
    } else {
      // Apply default medium size
      setFontSize('medium')
    }

    const savedFontFamily = localStorage.getItem('yolal-font-family')
    if (savedFontFamily) {
      fontFamily.value = savedFontFamily
      document.documentElement.setAttribute('data-font-family', savedFontFamily)
      applyFontFamily(savedFontFamily)
    }
  }

  return {
    sidebarCollapsed,
    sidebarOpened,
    language,
    theme,
    fontSize,
    fontFamily,
    fontSizeOptions,
    fontFamilyOptions,
    loading,
    loadingText,
    clubInfo,
    isDark,
    currentFontSize,
    currentFontFamily,
    toggleSidebar,
    toggleSidebarMobile,
    closeSidebarMobile,
    openSidebarMobile,
    setLanguage,
    setTheme,
    setFontSize,
    setFontFamily,
    showLoading,
    hideLoading,
    setClubInfo,
    init
  }
})
